import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { toast } from 'sonner';

import { RainmakerCard } from '@/components/rainmaker';
import {
  RainmakerEvidenceType,
  RainmakerStatus,
  RainmakerSuggestion,
  RainmakerSuggestionStatus,
} from '@/services/types/rainmaker-types';

// Initial mock data - all pending
const initialMockSuggestions: RainmakerSuggestion[] = [
  {
    id: '1',
    title: 'LOA Received',
    description:
      'An LOA was detected and linked to this matter. Mark task as complete?',
    cta_label: 'Complete Task',
    status: RainmakerSuggestionStatus.PENDING,
    generator: 'LOAProcessor',
    actions: [
      {
        type: 'UPDATE_TASK_STATUS',
        params: { task_id: 'task-123', status: 'COMPLETED' },
        description: 'Mark task as Completed',
      },
    ],
    evidence: [
      {
        type: RainmakerEvidenceType.FRONT_CONVERSATION,
        id: 'conv_abc123',
        display_name: 'Email from State Farm',
        metadata: {
          response_type: 'accepted',
          claim_number: 'CLM-2024-12345',
        },
      },
    ],
  },
  {
    id: '2',
    title: 'Medical Records Received',
    description:
      'Medical records for Dr. Smith have been received. Review and close task?',
    cta_label: 'Review & Complete',
    status: RainmakerSuggestionStatus.PENDING,
    generator: 'MedicalRecordsProcessor',
    actions: [
      {
        type: 'ATTACH_RECORDS_TO_PROVIDER',
        params: { provider_id: 'prov-456', blob_ids: ['blob-1', 'blob-2'] },
        description: 'Attach 2 PDF files to Dr. Smith',
      },
      {
        type: 'UPDATE_TASK_STATUS',
        params: { task_id: 'task-456', status: 'COMPLETED' },
        description: 'Mark task as Completed',
      },
    ],
    evidence: [
      {
        type: RainmakerEvidenceType.BLOB,
        id: 'blob-1',
        display_name: 'Medical Records Pt1.pdf',
        metadata: {
          file_size: '2.4 MB',
        },
      },
      {
        type: RainmakerEvidenceType.BLOB,
        id: 'blob-2',
        display_name: 'Medical Records Pt2.pdf',
        metadata: {
          file_size: '1.8 MB',
        },
      },
      {
        type: RainmakerEvidenceType.FRONT_CONVERSATION,
        id: 'conv_xyz789',
        display_name: 'Email from Dr. Smith',
        metadata: {
          sender: 'records@drsmith.com',
        },
      },
    ],
  },
  {
    id: '3',
    title: 'Police Report Available',
    description:
      'Police report has been purchased and is ready for review. Attach to matter?',
    cta_label: 'Attach Report',
    status: RainmakerSuggestionStatus.PENDING,
    generator: 'PoliceReportProcessor',
    actions: [
      {
        type: 'CREATE_POLICE_REPORT_ARTIFACT',
        params: { matter_id: 'matter-789', report_id: 'report-123' },
        description: 'Create police report artifact',
      },
      {
        type: 'UPDATE_TASK_STATUS',
        params: { task_id: 'task-789', status: 'COMPLETED' },
        description: 'Mark task as Completed',
      },
    ],
    evidence: [
      {
        type: RainmakerEvidenceType.DOCUMENT,
        id: 'doc-police-123',
        display_name: 'Police Report #2024-5678',
        metadata: {
          department: 'LAPD',
        },
      },
    ],
  },
];

export const RainmakerPreview = () => {
  const [suggestions, setSuggestions] = useState<RainmakerSuggestion[]>(
    initialMockSuggestions,
  );
  const [status, setStatus] = useState<RainmakerStatus>(RainmakerStatus.ACTIVE);

  // Calculate status based on suggestion states
  const calculateStatus = (
    currentSuggestions: RainmakerSuggestion[],
  ): RainmakerStatus => {
    const hasExecuted = currentSuggestions.some(
      (s) => s.status === RainmakerSuggestionStatus.EXECUTED,
    );
    const allDismissed = currentSuggestions.every(
      (s) => s.status === RainmakerSuggestionStatus.DISMISSED,
    );

    if (hasExecuted) return RainmakerStatus.DONE;
    if (allDismissed) return RainmakerStatus.IGNORED;
    return RainmakerStatus.ACTIVE;
  };

  const handleExecute = async (suggestionId: string) => {
    // Simulate API call with delay
    await new Promise<void>((resolve) => {
      setTimeout(() => resolve(), 1500);
    });

    // Update the suggestion status
    const updatedSuggestions = suggestions.map((s) =>
      s.id === suggestionId
        ? {
            ...s,
            status: RainmakerSuggestionStatus.EXECUTED,
            acted_at: new Date().toISOString(),
          }
        : s,
    );

    setSuggestions(updatedSuggestions);
    toast.success('Suggestion executed successfully!');

    // After a short delay, transition to DONE state
    setTimeout(() => {
      setStatus(calculateStatus(updatedSuggestions));
    }, 800);
  };

  const handleDismiss = async (suggestionId: string) => {
    // Simulate API call with delay
    await new Promise<void>((resolve) => {
      setTimeout(() => resolve(), 500);
    });

    // Update the suggestion status
    const updatedSuggestions = suggestions.map((s) =>
      s.id === suggestionId
        ? {
            ...s,
            status: RainmakerSuggestionStatus.DISMISSED,
            acted_at: new Date().toISOString(),
          }
        : s,
    );

    setSuggestions(updatedSuggestions);
    toast.success('Suggestion dismissed');

    // After a short delay, check if we should transition to IGNORED
    setTimeout(() => {
      setStatus(calculateStatus(updatedSuggestions));
    }, 600);
  };

  const handleRestore = async () => {
    // Simulate API call with delay
    await new Promise<void>((resolve) => {
      setTimeout(() => resolve(), 800);
    });

    // Reset all dismissed suggestions to pending
    const restoredSuggestions = suggestions.map((s) =>
      s.status === RainmakerSuggestionStatus.DISMISSED
        ? {
            ...s,
            status: RainmakerSuggestionStatus.PENDING,
            acted_at: undefined,
          }
        : s,
    );

    setSuggestions(restoredSuggestions);
    setStatus(RainmakerStatus.ACTIVE);
    toast.success('Suggestions restored to active state');
  };

  const handleReset = () => {
    setSuggestions(initialMockSuggestions);
    setStatus(RainmakerStatus.ACTIVE);
    toast.info('Preview reset to initial state');
  };

  // Helper to get status badge styling
  const getStatusBadgeColor = () => {
    if (status === RainmakerStatus.ACTIVE) {
      return {
        bgcolor: 'primary.50',
        borderColor: 'primary.200',
        textColor: 'primary.700',
      };
    }
    if (status === RainmakerStatus.DONE) {
      return {
        bgcolor: 'success.50',
        borderColor: 'success.200',
        textColor: 'success.700',
      };
    }
    return {
      bgcolor: 'grey.100',
      borderColor: 'grey.300',
      textColor: 'grey.700',
    };
  };

  const statusColors = getStatusBadgeColor();

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        bgcolor: 'grey.50',
        p: 3,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: '400px' }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 600, fontSize: '16px' }}
          >
            Rainmaker Preview
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: '12px' }}
          >
            Simulate real-world state transitions
          </Typography>
        </Box>

        {/* Current Status Indicator */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mb: 1,
              fontSize: '10px',
              fontWeight: 600,
              textTransform: 'uppercase',
              color: 'text.secondary',
              letterSpacing: '0.05em',
            }}
          >
            Current Status
          </Typography>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.5,
              bgcolor: statusColors.bgcolor,
              borderRadius: 1,
              border: 1,
              borderColor: statusColors.borderColor,
            }}
          >
            <Typography
              sx={{
                fontSize: '11px',
                fontWeight: 600,
                color: statusColors.textColor,
              }}
            >
              {status}
            </Typography>
          </Box>
        </Box>

        <div className="space-y-3">
          <RainmakerCard
            status={status}
            suggestions={suggestions}
            matterId="preview-matter-id"
            onExecute={handleExecute}
            onDismiss={handleDismiss}
            onRestore={handleRestore}
          />
        </div>

        {/* Reset Button */}
        <Box sx={{ mt: 3 }}>
          <button
            type="button"
            onClick={handleReset}
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Reset to Initial State
          </button>
        </Box>

        {/* Design Notes */}
        <Box
          sx={{
            mt: 4,
            p: 2,
            bgcolor: 'white',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{ fontWeight: 600, fontSize: '11px' }}
          >
            How to Test
          </Typography>
          <ul className="mt-1.5 space-y-0.5 text-[10px] leading-relaxed text-gray-600">
            <li>• Start in ACTIVE state with 3 pending suggestions</li>
            <li>• Execute any suggestion → transitions to DONE after delay</li>
            <li>• Dismiss all suggestions → transitions to IGNORED</li>
            <li>• Click Restore in IGNORED state → back to ACTIVE</li>
            <li>• Click Reset to start over with fresh data</li>
            <li>• Test evidence modal and execution modal workflows</li>
          </ul>
        </Box>
      </Box>
    </Box>
  );
};
